import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { resolveBtlCaseDisplayName } from '@api/btlApplicationForm/borrowerHelpers';
import type { BtlApplication } from '@api/btlApplicationForm/types';
import { ApplicationStatus } from '@api/btlApplicationForm/types';
import { buildBtlCaseSidebar } from '@components/CaseSidebar/buildCaseSidebar';
import { CaseSidebar } from '@components/CaseSidebar/components/CaseSidebar';
import {
  mapCaseSidebarItemToUrlSection,
  resolveCaseSidebarItemId,
} from '@components/CaseSidebar/navigation/formSectionMapping';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { useBtlMissingFields } from '@queries/btlApplications/useBtlMissingFields';
import { STATUS_TO_LABELS } from './constants';

interface BtlCaseSidebarProps {
  data?: BtlApplication;
  isLoading: boolean;
  isAffordabilityPassed?: boolean;
  isAffordabilityRunRequired?: boolean;
  currentSection?: string;
  onDisableTopSlicing: () => void;
}

function getBtlCaseSidebarStatusLabel(
  status: ApplicationStatus,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string {
  if (status === ApplicationStatus.NEW) {
    return formatMessage({ id: 'caseSidebar.status.newApplication' });
  }

  return STATUS_TO_LABELS[status as keyof typeof STATUS_TO_LABELS] ?? status;
}

function getCaseSummaryTitle(data: BtlApplication): string {
  return resolveBtlCaseDisplayName(data);
}

export function BtlCaseSidebar({
  data,
  isLoading,
  isAffordabilityPassed,
  isAffordabilityRunRequired,
  currentSection,
  onDisableTopSlicing,
}: BtlCaseSidebarProps) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { data: missingFieldsRes, refetch: refetchMissingFields } = useBtlMissingFields(data?.id ?? '');
  const activeItemId = resolveCaseSidebarItemId(currentSection) ?? CaseSidebarItemId.OVERVIEW;

  useEffect(() => {
    refetchMissingFields();
  }, [
    currentSection,
    data?.borrower?.individuals,
    data?.borrower?.companies,
    data?.applicationProperty?.status,
    refetchMissingFields,
  ]);

  const handleItemSelect = (itemId: CaseSidebarItemId) => {
    navigate({ search: `?section=${mapCaseSidebarItemToUrlSection(itemId)}` });
  };

  if (!data) {
    return (
      <CaseSidebar
        overviewItem={{
          id: CaseSidebarItemId.OVERVIEW,
          title: formatMessage({ id: 'caseSidebar.pages.overview' }),
          subTitle: '',
          isDisabled: false,
          component: null,
          warnings: [],
        }}
        state={{ sections: [], isTopSlicing: false }}
        isLoading={isLoading}
        headerTitle={formatMessage({ id: 'btlApplicationForm.createCase.label' })}
      />
    );
  }

  const { overviewItem, state } = buildBtlCaseSidebar({
    application: data,
    formatMessage,
    isAffordabilityPassed,
    isAffordabilityRunRequired,
    missingFields: missingFieldsRes?.missingFields,
    isMissingFieldsComplete: missingFieldsRes?.isComplete,
    householdsToHasIndividuals: missingFieldsRes?.householdsToHasIndividuals,
    activeItemId,
    components: {},
  });

  return (
    <CaseSidebar
      overviewItem={overviewItem}
      state={state}
      activeItemId={activeItemId}
      onItemSelect={handleItemSelect}
      headerTitle={getCaseSummaryTitle(data)}
      referenceNumber={data.referenceNumber}
      statusLabel={getBtlCaseSidebarStatusLabel(data.status, formatMessage)}
      application={data}
      isLoading={isLoading}
      showDisableTopSlicingButton={data.isTopSlicing}
      onDisableTopSlicing={onDisableTopSlicing}
    />
  );
}
